// Print a lab report.
//
// The on-screen modal is a working surface — status pills, the observation
// editor, the ABHA panel — none of which belongs on paper. So the PDF is built
// from the data rather than captured from that DOM: the same identification box
// and letterhead the radiology report and the discharge summary carry, then one
// block per test.
//
// The page box, typography and page breaking live in utils/reportSheet.js; the
// header/footer stamped onto every page in utils/reportLetterhead.js.
import { toast } from "react-toastify";
import {
  buildIdBox,
  esc,
  formatReportDate,
  renderReportSheet,
} from "../../../utils/reportSheet";
import { resolveFacilityDetails, resolveLogo } from "../../../utils/reportLetterhead";

const REFERENCE_COLUMNS = ["Parameter", "Result", "Unit", "Reference Range"];

// One test: its heading, then either the parameter table or a note explaining
// why there is none.
//
// Nothing sits between the two. Order number, order date, referring doctor and
// technician are already stated once in the identification box at the top — on
// a single-test report they were printing twice, one line apart — and the CPT
// is a billing code, not something the patient's copy needs to carry.
const testBlock = (order, index) => {
  const rows = Array.isArray(order?.rows) ? order.rows : [];

  let body;
  if (rows.length > 0) {
    body = `
      <table>
        <tbody>
          <tr>${REFERENCE_COLUMNS.map((c) => `<th>${c}</th>`).join("")}</tr>
          ${rows
            .map(
              (r) => `<tr>
                <td>${esc(r?.param)}</td>
                <td><strong>${esc(r?.value)}</strong></td>
                <td>${esc(r?.unit || "-")}</td>
                <td>${esc(r?.refRange || "-")}</td>
              </tr>`,
            )
            .join("")}
        </tbody>
      </table>`;
  } else {
    // Deliberately says nothing about the workflow stage — that belongs on the
    // orders dashboard, not on the document handed to a patient.
    body = `<p><em>Results awaited.</em></p>`;
  }

  return `
    <h3>${index + 1}. ${esc(order?.service)}</h3>
    ${body}
    ${
      order?.interpretation
        ? `<p><strong>Interpretation:</strong><br/>${esc(order.interpretation).replace(/\n/g, "<br/>")}</p>`
        : ""
    }
  `;
};

// Names arrive bare ("Sruti Mehra"). The guard keeps a feed that already sends
// a title from stuttering into "Dr. Dr.". Same rule the radiology report
// applies (radiologyOrdersShared.withSalutation).
export const withDoctorPrefix = (value) => {
  const name = String(value || "").trim();
  if (!name || name === "-") return "";
  return /^(dr|doctor|prof|mr|mrs|ms|mx)\b\.?/i.test(name) ? name : `Dr. ${name}`;
};

// The referring doctor reaches the report on the order ("doctor"), but the bulk
// lab list often leaves that blank and names the doctor once per admission
// instead — which is why "Ref. By Doctor" printed a bare "-". Fall back to the
// admission's doctor before giving up.
export const resolveReferringDoctor = (primary, patient) => {
  const name = [primary?.doctor, patient?.doctorName, patient?.doctor]
    .map((v) => String(v || "").trim())
    .find((v) => v && v !== "-");
  return withDoctorPrefix(name);
};

export function buildLabReportHtml({ patient, orders, generatedAt, technician }) {
  const list = Array.isArray(orders) ? orders : [];
  const primary = list[0] || {};
  const preparedBy = String(technician || primary?.technician || "").trim();

  return `
    <div class="rp-title">Laboratory Report</div>
    ${buildIdBox(
      [
        ["Patient Name", patient?.name],
        ["Patient ID", patient?.uhid],
        ["Age", patient?.age || patient?.age === 0 ? `${patient.age} Y` : "-"],
        ["Ref. By Doctor", resolveReferringDoctor(primary, patient)],
        ["Created By", preparedBy],
      ],
      [
        ["Order No.", primary?.orderNo],
        ["Order Date", formatReportDate(primary?.ordered)],
        ["Report Date", formatReportDate(generatedAt || Date.now())],
        ["Total Tests", String(list.length)],
      ],
    )}
    <div class="rp-body">
      ${list.length ? list.map(testBlock).join("") : "<p>No tests on this report.</p>"}
    </div>
  `;
}

/**
 * Render the lab report to a PDF Blob.
 *
 * @param {object}  args
 * @param {object}  args.patient    { name, uhid, age, gender, dob }
 * @param {Array}   args.orders     one entry per test, already resolved by the
 *   caller: { orderNo, service, cpt, ordered, doctor, technician, status,
 *   pending, rows: [{param, value, unit, refRange}], interpretation }
 * @param {string}  args.technician      salutation + name as typed on the report
 *   screen. Prints as "Created By" in the identification box and over the
 *   "Prepared By" rule in the letterhead footer.
 * @param {Array}   args.brandingData    BrandingContext value — the facility's
 *   configurable logo. Omit and the bundled logo is used.
 * @param {object}  args.userInfo        PermissionContext's `userInfo` — supplies
 *   the facility address, the same source the billing invoice prints from.
 * @param {boolean} args.withLetterhead  false prints the content alone, for
 *   hospitals feeding pre-printed stationery through the tray.
 * @param {boolean} args.autoPrint       ask the viewer to open its print dialog.
 */
export function generateLabReportPdf({
  patient,
  orders,
  generatedAt,
  technician,
  brandingData,
  userInfo,
  withLetterhead = true,
  autoPrint = false,
}) {
  const safeName = String(patient?.name || "Patient").replace(/[^a-zA-Z0-9]/g, "_");
  const { address, contact } = resolveFacilityDetails(userInfo);
  const primary = (Array.isArray(orders) ? orders : [])[0] || {};
  // Typed on the report screen; falls back to whatever the order carried for
  // hospitals that get it from the backend instead.
  const preparedBy = withDoctorPrefix(technician || primary.technician);

  return renderReportSheet({
    html: buildLabReportHtml({ patient, orders, generatedAt, technician }),
    filename: `Lab_Report_${safeName}_${primary.orderNo || ""}.pdf`,
    autoPrint,
    letterhead: withLetterhead
      ? {
          logoBase64: resolveLogo(brandingData),
          address,
          contact,
          patientId: patient?.uhid,
          // The technician who ran the samples signs as preparer; "Checked By"
          // is left for the pathologist's counter-signature on paper.
          preparedBy,
          checkedBy: "",
        }
      : null,
  });
}

/**
 * Save the report as a file. Deliberately separate from printLabReport: that
 * one opens a viewer tab with autoPrint set, which is a print action, not a
 * download — wiring the Download button to it made the two buttons do the same
 * thing.
 */
export async function downloadLabReport(args) {
  try {
    const blob = await generateLabReportPdf({ ...args, autoPrint: false });
    const safeName = String(args?.patient?.name || "Patient").replace(
      /[^a-zA-Z0-9]/g,
      "_",
    );
    const orderNo = (Array.isArray(args?.orders) ? args.orders : [])[0]?.orderNo;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Lab_Report_${safeName}_${orderNo || ""}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Same reason as below: revoking synchronously can abort the save in some
    // browsers before it has read the blob.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    console.error("Could not generate the lab report PDF:", err);
    toast.error("Could not download the report PDF. Please try again.");
  }
}

export async function printLabReport(args) {
  try {
    const blob = await generateLabReportPdf({ ...args, autoPrint: true });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    if (!win) {
      URL.revokeObjectURL(url);
      toast.error("Please allow pop-ups to print the report");
      return;
    }
    // Revoking immediately would pull the file out from under the viewer.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    console.error("Could not generate the lab report PDF:", err);
    toast.error("Could not generate the report PDF. Please try again.");
  }
}
